import { EXPRESS_KEYS } from '@src/queries/Express'
import { ExpressKeys } from '@src/queries/ExpressKeys'
import { CheckWithMetadata } from '@src/types/checks'
import graphApi from '@src/utils/http-clients/graphApi'
import { getJwt } from '@src/utils/localStorage'
import { createLogic } from 'redux-logic'
import { filter, takeUntil, tap } from 'rxjs/operators'
import { getType } from 'typesafe-actions'
import { checksCancelled, checksComplete } from '../checks/actions'
import { getWtaiUrl } from '../site/selectors'
import site from '../site/site'
import { RootState } from '../types'
import {
  createPusherChannel,
  handleCheckerErrors,
  handlePusherMessage,
  openPusherConnection,
  pusherAnalyze,
  pusherAuthError,
  pusherConnected,
  pusherDisconnected,
  pusherHandleChecks,
  PusherPlagiarismBasePayload,
} from './actions'
import { bindWithChunkingFuture } from './bindChannelChunking'
import { PusherData } from './types'

export const WTAI_EXPRESS_URL = '/express/api/v1'

const BASE_URL = getWtaiUrl(site) + WTAI_EXPRESS_URL
export interface KeysResponse {
  channelName: string
  traceId: string
  appKey: string
  cluster: string
}

// PUSHER CHANNEL EVENTS
export const CHANNEL_CONNECTED_EVENT = 'pusher:subscription_succeeded'
export const CHANNEL_ANALYZE_EVENT = 'analyze'
export const CHANNEL_ERROR_EVENT = 'pusher:subscription_error'

// PUSHER GLOBAL EVENTS
export const PUSHER_STATE_CHANGE_EVENT = 'state_change'
export const PUSHER_CONNECTED_EVENT = 'connected'
export const PUSHER_STATE_DISCONNECTED = 'disconnected'
export const PUSHER_STATE_UNAVAILABLE = 'unavailable'
export const PUSHER_STATE_CONNECTING = 'connecting'

export type PUSHER_STATES = typeof PUSHER_STATE_DISCONNECTED &
  typeof PUSHER_STATE_UNAVAILABLE &
  typeof PUSHER_STATE_CONNECTING &
  typeof PUSHER_CONNECTED_EVENT

export interface PusherStateChange {
  previous: PUSHER_STATES
  current: PUSHER_STATES
}

export const pusherConnectLogic = createLogic<RootState, PusherPlagiarismBasePayload>({
  type: getType(openPusherConnection),
  processOptions: {
    dispatchMultiple: true,
  },
  cancelType: [getType(checksComplete), getType(checksCancelled)],
  warnTimeout: 0,
  async process({ action$, action, cancelled$ }, dispatch, done) {
    const {
      data: { expressKeys },
    } = await graphApi.query<ExpressKeys>({
      query: EXPRESS_KEYS,
      fetchPolicy: 'network-only',
    })
    const { isPlagiarism, origin, isRecheck } = action.payload
    import('pusher-js').then(async Pusher => {
      // @ts-ignore
      const pusher = new Pusher(expressKeys.appKey, {
        authEndpoint: `${BASE_URL}/auth`,
        auth: {
          headers: {
            Authorization: `Bearer ${getJwt()}`,
          },
        },
        cluster: expressKeys.cluster,
      })

      const createChannel = async (
        channelNameArg?: string,
        traceIdArg?: string,
        isPlag?: boolean,
        isRecheck?: boolean
      ) => {
        let channelName = channelNameArg
        let traceId = traceIdArg

        try {
          if (!channelName) {
            const {
              data: { expressKeys },
            } = await graphApi.query<ExpressKeys>({
              query: EXPRESS_KEYS,
              fetchPolicy: 'network-only',
            })

            channelName = expressKeys.channelName
            traceId = expressKeys.traceId
          }
        } catch (error) {
          throw new Error('Could not getKeys while creating new channel')
        }

        const channel = pusher.subscribe(channelName)

        channel.bind(CHANNEL_CONNECTED_EVENT, () => {
          if (traceId) {
            dispatch(
              pusherAnalyze({
                origin,
                traceId,
                isRecheck,
                isPlagiarism: isPlag,
              })
            )
          }
        })

        // This is where we receive checks on the 'analyze' event
        bindWithChunkingFuture(
          channel,
          CHANNEL_ANALYZE_EVENT,
          (expressKeys: PusherData<CheckWithMetadata>) => {
            dispatch(handlePusherMessage(expressKeys))
          }
        )

        channel.bind(CHANNEL_ERROR_EVENT, (status: number) => {
          if (status === 408 || status === 503) {
            // retry?
            // Still need to decide on what should happen here
            dispatch(pusherAuthError())
          }
        })
      }

      await createChannel(
        expressKeys.channelName,
        expressKeys.traceId,
        isPlagiarism,
        isRecheck
      )

      action$
        .pipe(
          filter(action => action.type === getType(createPusherChannel)),
          tap(async action => {
            createChannel(
              undefined,
              undefined,
              action.payload.isPlagiarism,
              action.payload.isRecheck
            )
          }),
          takeUntil(cancelled$)
        )
        .subscribe()

      cancelled$.subscribe(() => {
        pusher.disconnect()
      })

      pusher.connection.bind(PUSHER_STATE_CHANGE_EVENT, (state: PusherStateChange) => {
        if (
          state.current === PUSHER_STATE_DISCONNECTED ||
          state.current === PUSHER_STATE_UNAVAILABLE
        ) {
          dispatch(pusherDisconnected())
        }
      })

      pusher.connection.bind(PUSHER_CONNECTED_EVENT, () => {
        dispatch(
          pusherConnected({ appKey: expressKeys.appKey, cluster: expressKeys.cluster })
        )
      })
    })
  },
})

export const handlePusherMessageLogic = createLogic<
  RootState,
  PusherData<CheckWithMetadata>
>({
  type: getType(handlePusherMessage),
  process({ action }, dispatch, done) {
    const { error, message } = action.payload
    if (message) {
      dispatch(pusherHandleChecks(message))
    } else if (error) {
      dispatch(handleCheckerErrors(error))
    }
    done()
  },
})

export default [pusherConnectLogic, handlePusherMessageLogic]



// WEBPACK FOOTER //
// ./src/state/pusher/logic.ts